<template>
  <div>
    <div class="order-pagination">
      <va-pagination
        v-model="currentPage"
        :page-size="15"
        :pages="orders.meta.last_page"
      />
    </div>
    <va-card>
      <div class="flex search-by-id mb-4">
        <div class="orders-filters">
          <va-select
            v-model="order_type"
            class="mb-4 mr-3"
            label="Тип заказа"
            :options="options"
            track-by="id"
          />
          <va-select
            v-model="order_status_filter"
            class="mb-4 mr-3"
            label="Статус заказа"
            :options="orderStatusOptions"
            track-by="id"
          />
        </div>

        <va-input
          v-model="searchTerm"
          class="mb-4"
          label="Поиск по номеру заказа"
          placeholder="Введите номер заказа"
          type="number"
          removable
        >
          <va-icon slot="prepend" name="fa fa-search" />
        </va-input>
      </div>
      <va-data-table
        :fields="fields"
        :data="orders.data"
        :loading="loading"
        :row-class="getOrderRowClass"
        class="va-table va-table--hoverable va-table--clickable orders-table"
        api-mode
        no-data-label="Заказы не найдены"
        @row-clicked="orderPreview"
      >
        <template slot="id" slot-scope="props">
          <div class="order-id-cell">
            <va-button
              v-if="canEditOrder(props.rowData)"
              flat
              small
              color="gray"
              icon="fa fa-pencil"
              class="order-id-edit"
              title="Редактировать заказ"
              @click.stop="goToEdit(props.rowData)"
            />
            <span class="order-id">#{{ props.rowData.id }}</span>
          </div>
        </template>
        <template slot="order_type" slot-scope="props">
          <span
            :class="getDeliveryMethodClass(props.rowData)"
            class="delivery-method-badge"
          >
            {{ getDeliveryMethodText(props.rowData) }}
          </span>
        </template>
        <template slot="created_date" slot-scope="props">
          <span v-if="props.rowData.created_at" class="order-date">
            {{ formatDateOnly(props.rowData.created_at) }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>
        <template slot="created_time" slot-scope="props">
          <span v-if="props.rowData.created_at" class="order-time">
            {{ formatTimeOnly(props.rowData.created_at) }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>
        <template slot="date_order" slot-scope="props">
          <span v-if="props.rowData.date_delivery !== null" class="order-date">
            {{ formatDate(props.rowData.date_delivery) }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>
        <template slot="name" slot-scope="props">
          <span class="order-name">{{ props.rowData.name || "—" }}</span>
        </template>
        <template slot="user_id" slot-scope="props">
          <span v-if="props.rowData.user_id" class="user-id">
            ID: {{ props.rowData.user_id }}
          </span>
          <span v-else class="text-muted">Неавторизован</span>
        </template>
        <template slot="phone" slot-scope="props">
          <span class="order-phone">{{
            formatPhone(props.rowData.phone)
          }}</span>
        </template>
        <template slot="count_of_products" slot-scope="props">
          <div class="products-info">
            <div class="products-count">
              <strong
                >Кол-во:
                {{
                  props.rowData.products ? props.rowData.products.length : 0
                }}</strong
              >
            </div>
            <div
              v-if="props.rowData.products && props.rowData.products.length > 0"
              class="products-list"
            >
              <div
                v-for="item in props.rowData.products"
                :key="item.id"
                class="product-item"
              >
                <span class="product-name">{{
                  item.name || (item.product && item.product.name) || "—"
                }}</span>
                <span class="product-qty">x{{ item.qty }}</span>
                <span v-if="item.reserved" class="badge badge-warning"
                  >Зарезервировано</span
                >
              </div>
            </div>
          </div>
        </template>

        <template slot="delivery_address" slot-scope="props">
          <template
            v-if="
              props.rowData.additional_data &&
              props.rowData.additional_data.quick_buy
            "
          >
            <span>Требует уточнения</span>
          </template>
          <template v-else-if="props.rowData.delivery_type === 'pickup'">
            <span
              v-if="
                (props.rowData.pickup_location &&
                  props.rowData.pickup_location.address) ||
                props.rowData.pickup_address
              "
              v-tooltip="
                (props.rowData.pickup_location &&
                  props.rowData.pickup_location.address) ||
                props.rowData.pickup_address
              "
              >{{
                ((props.rowData.pickup_location &&
                  props.rowData.pickup_location.address) ||
                  props.rowData.pickup_address) | truncate(15)
              }}</span
            >
            <span v-else class="text-muted">Самовывоз</span>
          </template>
          <template v-else-if="props.rowData.delivery_type === 'delivery'">
            <span
              v-if="props.rowData.full_address"
              v-tooltip="props.rowData.full_address"
              >{{ props.rowData.full_address | truncate(15) }}</span
            >
            <span v-else class="text-muted">—</span>
          </template>
          <template v-else>
            <!-- Fallback для старых заказов без delivery_type -->
            <template
              v-if="
                props.rowData.full_address === null &&
                props.rowData.additional_data === null
              "
            >
              <span>Самовывоз</span>
            </template>
            <template v-else>
              <span
                v-if="props.rowData.full_address"
                v-tooltip="props.rowData.full_address"
                >{{ props.rowData.full_address | truncate(15) }}</span
              >
              <span v-else class="text-muted">—</span>
            </template>
          </template>
        </template>
        <template slot="total_price" slot-scope="props">
          <span class="order-price">{{
            formatPrice(props.rowData.total_discounted_amount)
          }}</span>
        </template>

        <template slot="payment_status_cell" slot-scope="props">
          <div class="status-container">
            <span
              :class="getPaymentStatusClass(props.rowData.payment_status)"
              class="status-badge status-badge--clickable"
              role="button"
              tabindex="0"
              title="Изменить статусы"
              @click.stop="changeStatus(props.rowData)"
              @keyup.enter.stop="changeStatus(props.rowData)"
              @keyup.space.prevent.stop="changeStatus(props.rowData)"
            >
              {{ getPaymentStatusText(props.rowData.payment_status) }}
            </span>
          </div>
        </template>

        <template slot="order_status_cell" slot-scope="props">
          <div class="status-container">
            <span
              :class="
                getOrderStatusClass(
                  props.rowData.order_status || props.rowData.status
                )
              "
              class="status-badge status-badge--clickable"
              role="button"
              tabindex="0"
              title="Изменить статусы"
              @click.stop="changeStatus(props.rowData)"
              @keyup.enter.stop="changeStatus(props.rowData)"
              @keyup.space.prevent.stop="changeStatus(props.rowData)"
            >
              {{
                getOrderStatusText(
                  props.rowData.order_status || props.rowData.status
                )
              }}
            </span>
          </div>
        </template>
      </va-data-table>
    </va-card>

    <va-modal
      v-model="showOrderModal"
      size="large"
      class="order-preview-modal"
      hide-default-actions
    >
      <order-preview :order-info="currOrder" @hide="hideOrderModal" />
    </va-modal>
    <va-modal v-model="showOrderStatusModal" size="large" hide-default-actions>
      <order-status
        :order-info="currOrder"
        @hide="hideOrderStatusModal"
        @saved="onOrderStatusSaved"
      />
    </va-modal>
    <va-modal v-model="showApproveOrderModal" size="large" hide-default-actions>
      <order-disapprove-modal
        :order-info="currOrder"
        @hide="showApproveOrderModal = false"
      />
    </va-modal>

    <div class="order-pagination">
      <va-pagination
        v-model="currentPage"
        :page-size="15"
        :pages="orders.meta.last_page"
      />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import orderPreview from "./order-preview.vue";
import OrderStatus from "./order-status.vue";
import { debounce } from "lodash";
import api from "@/api/axios";
import {
  applyOrderViewedAt,
  markOrderViewed,
} from "@/utils/orderViewed";

import { VTooltip, VPopover, VClosePopover } from "v-tooltip";
import { mapActions } from "vuex";
Vue.directive("tooltip", VTooltip);
Vue.directive("close-popover", VClosePopover);
Vue.component("v-popover", VPopover);
export default {
  components: { orderPreview, OrderStatus },
  filters: {
    truncate(value, size) {
      if (!value) return "";
      value = value.toString();

      if (value.length <= size) {
        return value;
      }
      return value.substr(0, size) + "...";
    },
  },
  props: {
    orders: {
      type: Object,
      require: true,
      default: () => {},
    },
  },
  data() {
    return {
      showOrderModal: false,
      showOrderStatusModal: false,
      showApproveOrderModal: false,
      currOrder: {},
      term: null,
      searchTerm: "",
      pageNum: 1,
      value: 1,
      loading: false,
      order_type: {
        id: 1,
        text: "Обычные заказы",
        value: "0",
      },
      options: [
        {
          id: 1,
          text: "Обычные заказы",
          value: "0",
        },
        {
          id: 2,
          text: "Быстрые заказы",
          value: "1",
        },
      ],
      order_status_filter: {
        id: 2,
        text: "Активные",
        value: "active",
      },
      orderStatusOptions: [
        {
          id: 1,
          text: "Все",
          value: "all",
        },
        {
          id: 2,
          text: "Активные",
          value: "active",
        },
        {
          id: 3,
          text: "Неактивные",
          value: "inactive",
        },
        {
          id: 4,
          text: "Новый",
          value: "new",
        },
        {
          id: 5,
          text: "В работе",
          value: "in_work",
        },
        {
          id: 6,
          text: "Собирается",
          value: "assembling",
        },
        {
          id: 7,
          text: "Готов",
          value: "ready",
        },
        {
          id: 8,
          text: "Передан курьеру",
          value: "in_delivery",
        },
        {
          id: 9,
          text: "Завершен",
          value: "completed",
        },
        {
          id: 10,
          text: "Отменен",
          value: "cancelled",
        },
      ],
    };
  },
  computed: {
    fields() {
      return [
        {
          name: "__slot:id",
          title: "Номер",
          width: "128px",
        },
        {
          name: "__slot:order_type",
          title: "Способ получения",
          width: "150px",
        },
        {
          name: "__slot:created_date",
          title: "Дата",
          width: "120px",
        },
        {
          name: "__slot:created_time",
          title: "Время",
          width: "100px",
        },
        {
          name: "__slot:date_order",
          title: "Дата получения",
          width: "120px",
        },
        {
          name: "__slot:name",
          title: "Имя клиента",
          width: "150px",
        },
        {
          name: "__slot:user_id",
          title: "ID пользователя",
          width: "150px",
        },
        {
          name: "__slot:phone",
          title: "Телефон",
          width: "130px",
        },
        {
          name: "__slot:count_of_products",
          title: "Товары",
          width: "250px",
        },
        {
          name: "__slot:delivery_address",
          title: "Адрес доставки",
          width: "200px",
        },
        {
          name: "__slot:total_price",
          title: "Сумма",
          width: "120px",
        },
        {
          name: "__slot:payment_status_cell",
          title: "Оплата",
          width: "180px",
        },
        {
          name: "__slot:order_status_cell",
          title: "Заказ",
          width: "180px",
        },
      ];
    },
    currentPage: {
      get() {
        return this.orders.meta.current_page;
      },
      set(page) {
        this.pageNum = page;
      },
    },
  },
  watch: {
    pageNum() {
      this.updateList(this.buildFiltersPayload(this.pageNum));
    },
    searchTerm(newValue) {
      this.searchByNumber(newValue);
    },
    order_type(val) {
      this.pageNum = 1;
      this.updateList(this.buildFiltersPayload(1, val.value || 0));
    },
    order_status_filter() {
      this.pageNum = 1;
      this.updateList(this.buildFiltersPayload(1));
    },
  },
  methods: {
    ...mapActions("order", ["getOrders"]),
    getOrderStatusesByFilter() {
      const filterValue = this.order_status_filter?.value || "all";
      if (filterValue === "active") {
        return ["new", "in_work", "assembling", "ready", "in_delivery"];
      }
      if (filterValue === "inactive") {
        return ["completed", "cancelled"];
      }
      if (filterValue === "all") {
        return [];
      }
      return [filterValue];
    },
    buildFiltersPayload(page = 1, quickBuyValue = this.order_type.value || 0) {
      const payload = {
        id: this.term || null,
        page,
        is_quick_buy: quickBuyValue,
      };
      const orderStatuses = this.getOrderStatusesByFilter();
      if (orderStatuses.length) {
        payload.order_statuses = orderStatuses;
      }
      return payload;
    },
    formatOmsk(dateString, options) {
      if (!dateString) return "—";
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return "—";
      return date.toLocaleString("ru-RU", {
        timeZone: "Asia/Omsk",
        ...options,
      });
    },

    searchByNumber: debounce(function (value) {
      this.term = value || null;
      this.pageNum = 1;
      this.updateList(this.buildFiltersPayload(1));
    }, 1000),
    formatDate(dateString) {
      return this.formatOmsk(dateString, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    formatDateOnly(dateString) {
      return this.formatOmsk(dateString, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    formatTimeOnly(dateString) {
      return this.formatOmsk(dateString, {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getDeliveryMethodText(order) {
      // Используем новое поле delivery_type, если оно есть
      if (order.delivery_type === "pickup") {
        return "Самовывоз";
      }
      if (order.delivery_type === "delivery") {
        return "Доставка";
      }
      // Fallback для старых заказов без delivery_type
      if (
        order.full_address === null &&
        (!order.additional_data || order.additional_data === null)
      ) {
        return "Самовывоз";
      }
      return "Доставка";
    },
    getDeliveryMethodClass(order) {
      // Используем новое поле delivery_type, если оно есть
      if (order.delivery_type === "pickup") {
        return "method-pickup";
      }
      if (order.delivery_type === "delivery") {
        return "method-delivery";
      }
      // Fallback для старых заказов без delivery_type
      if (
        order.full_address === null &&
        (!order.additional_data || order.additional_data === null)
      ) {
        return "method-pickup";
      }
      return "method-delivery";
    },
    formatPhone(phone) {
      if (!phone) return "—";
      // Форматирование телефона: +7 (XXX) XXX-XX-XX
      const cleaned = phone.replace(/\D/g, "");
      if (cleaned.length === 11 && cleaned.startsWith("7")) {
        return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(
          4,
          7
        )}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
      }
      return phone;
    },
    formatPrice(amount) {
      if (!amount && amount !== 0) return "—";
      return new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "RUB",
        minimumFractionDigits: 0,
      }).format(amount);
    },
    getOrderStatusText(status) {
      const map = {
        new: "Новый",
        in_work: "В работе",
        assembling: "Собирается",
        ready: "Готов",
        in_delivery: "Передан курьеру",
        completed: "Завершён",
        cancelled: "Отменён",
      };
      return map[status] || status || "—";
    },
    getPaymentStatusText(paymentStatus) {
      const map = {
        pending_payment: "Не оплачен",
        waiting_for_paid_confirmation: "Ожидает подтверждения",
        paid: "Оплачен",
        refunding: "Возврат",
        refunded: "Возвращён",
      };
      return map[paymentStatus] || paymentStatus || "—";
    },
    getOrderStatusClass(status) {
      const map = {
        new: "status-pending",
        in_work: "status-warning",
        assembling: "status-warning",
        ready: "status-primary",
        in_delivery: "status-warning",
        completed: "status-primary",
        cancelled: "status-danger",
      };
      return map[status] || "";
    },
    getPaymentStatusClass(paymentStatus) {
      const map = {
        pending_payment: "status-pending",
        waiting_for_paid_confirmation: "status-pending",
        paid: "status-success",
        refunding: "status-warning",
        refunded: "status-primary",
      };
      return map[paymentStatus] || "";
    },
    async updateList(payload) {
      try {
        this.loading = true;
        await this.getOrders(payload);
      } catch (error) {
        return error;
      } finally {
        this.loading = false;
      }
    },
    async markRowViewed(orderInfo) {
      if (!orderInfo?.id || orderInfo.admin_viewed_at) {
        return orderInfo?.admin_viewed_at ?? null;
      }

      try {
        const viewedAt = await markOrderViewed(orderInfo.id);
        if (!viewedAt) {
          return null;
        }
        applyOrderViewedAt(this.orders?.data, orderInfo.id, viewedAt);
        if (Number(this.currOrder?.id) === Number(orderInfo.id)) {
          this.$set(this.currOrder, "admin_viewed_at", viewedAt);
        }
        this.$set(orderInfo, "admin_viewed_at", viewedAt);
        return viewedAt;
      } catch (error) {
        console.error("Ошибка при отметке заказа просмотренным:", error);
        return null;
      }
    },
    async orderPreview(orderInfo) {
      try {
        const response = await api.get(`/admin/orders/${orderInfo.id}`);
        this.currOrder = response?.data?.data || orderInfo;
        const viewedAt = this.currOrder.admin_viewed_at;
        if (viewedAt) {
          applyOrderViewedAt(this.orders?.data, orderInfo.id, viewedAt);
          this.$set(orderInfo, "admin_viewed_at", viewedAt);
        }
      } catch (error) {
        console.error("Ошибка при загрузке деталей заказа:", error);
        this.currOrder = orderInfo;
      }
      this.showOrderModal = true;
    },
    hideOrderModal() {
      this.showOrderModal = false;
    },
    hideOrderStatusModal() {
      this.showOrderStatusModal = false;
    },
    onOrderStatusSaved() {
      const page =
        this.orders?.meta?.current_page ?? this.pageNum ?? 1;
      this.pageNum = page;
      this.updateList(this.buildFiltersPayload(page));
    },
    async changeStatus(orderInfo) {
      await this.markRowViewed(orderInfo);
      this.currOrder = orderInfo;
      this.showOrderStatusModal = true;
    },
    canEditOrder(row) {
      if (!row) {
        return false;
      }
      const os = row.order_status != null ? row.order_status : row.status;
      if (!os || os === "cancelled" || os === "completed") {
        return false;
      }
      if (
        row.payment_status === "refunding" ||
        row.payment_status === "refunded"
      ) {
        return false;
      }
      return true;
    },
    async goToEdit(row) {
      if (!row || row.id == null) {
        return;
      }
      await this.markRowViewed(row);
      this.$router.push({
        name: "order-edit",
        params: { id: String(row.id) },
      });
    },
    getOrderRowClass(row) {
      if (!row || row.admin_viewed_at) {
        return "";
      }
      return "orders-table__row--unviewed";
    },
  },
};
</script>

<style lang="scss">
.order-pagination {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
  margin-top: 1rem;
}

.orders-table {
  .va-table__table {
    width: 100%;
  }
}

/* Подсветка непросмотренных заказов (глобально — таблица внутри va-data-table). */
tr.orders-table__row--unviewed > td,
tr.orders-table__row--unviewed:hover > td,
.va-table--striped tr.orders-table__row--unviewed:nth-child(even) > td,
.va-table--striped tr.orders-table__row--unviewed:nth-child(odd) > td,
.va-table--hoverable tr.orders-table__row--unviewed:hover > td {
  background-color: #bcf1b9 !important;
}

.order-preview-modal {
  .va-modal__container,
  .va-modal__dialog {
    width: min(1200px, 95vw);
    max-width: min(1200px, 95vw);
  }
}

@import "src/assets/scss/components/tooltip.scss";
</style>
<style lang="scss" scoped>
.order-id-cell {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.order-id-edit {
  flex-shrink: 0;
}

.order-id {
  font-weight: 600;
  color: #1976d2;
}

.user-id {
  font-weight: 500;
  color: #424242;
  font-size: 0.9rem;
}

.order-date {
  font-size: 0.9rem;
  color: #424242;
}

.order-time {
  font-size: 0.9rem;
  color: #424242;
  white-space: nowrap;
}

.delivery-method-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;

  &.method-delivery {
    background-color: #4bb543;
    color: #fff;
  }

  &.method-pickup {
    background-color: #ff9800;
    color: #fff;
  }
}

.order-name {
  font-weight: 500;
}

.order-phone {
  font-size: 0.9rem;
  color: #616161;
  white-space: nowrap;
}

.order-price {
  font-weight: 600;
  color: #1976d2;
  font-size: 1rem;
}

.products-info {
  max-width: 250px;

  .products-count {
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  .products-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .product-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;

    .product-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .product-qty {
      color: #757575;
      font-weight: 500;
    }
  }
}

.status-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;

  &.status-badge--clickable {
    cursor: pointer;
    user-select: none;
    outline: none;

    &:hover {
      filter: brightness(0.94);
    }

    &:focus-visible {
      box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.45);
    }
  }

  &.status-pending {
    background-color: #e0e0e0;
    color: #424242;
  }

  &.status-success {
    background-color: #4bb543;
    color: #fff;
  }

  &.status-warning {
    background-color: #f2f236;
    color: #000;
  }

  &.status-danger {
    background-color: #df4759;
    color: #fff;
  }

  &.status-primary {
    background-color: #0275d8;
    color: #fff;
  }
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 4px;
  margin-left: 6px;

  &-warning {
    background-color: #f2f236;
    color: #000;
  }

  &-success {
    background-color: #4bb543;
    color: #fff;
  }
}

.text-muted {
  color: #9e9e9e;
  font-style: italic;
}

@media only screen and (max-width: 768px) {
  .search-by-id {
    flex-direction: column;
    align-items: stretch;

    .va-select,
    .va-input {
      width: 100%;
      min-width: unset;
    }
  }

  .order-pagination {
    justify-content: center;

    .va-pagination {
      width: 100%;
      overflow: auto;
      white-space: nowrap;
    }
  }

  .orders-table {
    overflow-x: auto;
  }

  .status-container {
    flex-direction: column;
    align-items: flex-start;
  }
}

.orders-filters {
  display: flex;
}
</style>
